'use client';

import { Modal } from '@/components/Modal/Modal';
import { useSettingsContext } from '@/contexts/SettingsContext';
import {
    TypeCaseStudyWithoutUnresolvableLinksResponse,
    TypeProductContentHousingWithoutUnresolvableLinksResponse,
} from '@/features/contentful/_generated';
import CaseStudiesCard from '@/features/contentful/components/CaseStudiesCard';
import CaseStudiesModal from '@/features/contentful/components/CaseStudiesModal/CaseStudiesModal';
import TileContainer from '@/features/contentful/components/Tile/TileContainer';
import TileHeader from '@/features/contentful/components/Tile/TileHeader';
import { TPosition } from '@/types/types';
import classNames from 'classnames';
import React, { useState } from 'react';
import { GothamMedium } from '@/utils/fonts';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import SwiperCarousel from '@/components/Carousel/SwiperCarousel';

type TCaseStudiesContentProps = {
    component: TypeProductContentHousingWithoutUnresolvableLinksResponse;
    id?: string;
};

const ModalTitle = ({ component, id }: TCaseStudiesContentProps) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: id });

    return (
        <div
            className={classNames(
                'tw-text-pink1 tw-text-x tw-mb-[5px] tw-px-[20px] sm:tw-px-0',
                GothamMedium.className,
            )}
        >
            <span
                {...inspectorProps({
                    fieldId: 'modalTitleCategory',
                })}
            >
                {component.fields?.modalTitleCategory}
            </span>{' '}
            |{' '}
            <span
                {...inspectorProps({
                    fieldId: 'modalTitleSubCategory',
                })}
            >
                {component.fields?.modalTitleSubCategory}
            </span>
        </div>
    );
};

const CaseStudiesContent: React.FC<TCaseStudiesContentProps> = ({ component, id }) => {
    const { fields } = component;
    const { getLabel } = useSettingsContext();
    const LEARN_MORE_LABEL = getLabel('label', 'learnMore');
    const ourComponents = fields?.ourComponent as TypeCaseStudyWithoutUnresolvableLinksResponse[];
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState<
        TypeCaseStudyWithoutUnresolvableLinksResponse | undefined
    >(undefined);

    const handleModalOpen = (entityId: string) => {
        setIsModalOpen(true);
        setSelectedEntry(ourComponents.find((item) => item.sys.id === entityId));
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
    };

    return (
        <>
            <Modal
                isOpen={isModalOpen}
                handleClose={handleModalClose}
                classes={{
                    root: 'sm:tw-max-w-[500px] md:tw-max-w-[730px] lg:tw-max-w-[1080px] lg:tw-w-[90%] tw-bg-modalGrayBg tw-min-h-full sm:tw-min-h-0 tw-bg-modalGrayBg sm:tw-bg-white sm:tw-max-w-[500px] sm:tw-min-h-0',
                    container: 'tw-bg-modalGrayBg sm:tw-bg-white',
                }}
            >
                {selectedEntry && (
                    <CaseStudiesModal
                        data={selectedEntry}
                        handleModalClose={handleModalClose}
                        modalTitle={<ModalTitle component={component} id={id} />}
                    />
                )}
            </Modal>
            <TileContainer
                className={classNames({
                    'tw-bg-bgSkyLight': fields?.backgroundColor === 'blue',
                    'tw-bg-bgNavy': fields?.backgroundColor === 'dark blue',
                })}
                paddingTop={fields?.paddingTop}
                paddingBottom={fields?.paddingBottom}
            >
                <TileHeader
                    id={id}
                    title={fields?.title}
                    subtitle={fields?.subtitle}
                    isDarkMode={fields?.backgroundColor === 'dark blue'}
                    titlePosition={fields?.titlePosition as TPosition}
                    subtitlePosition={fields?.subtitlePosition as TPosition}
                />
                <SwiperCarousel
                    config={{
                        withArrowButtons: false,
                        autoplay: true,
                        paginationDotColor: '#7ce0d3',
                        multiCard: true,
                        type: 'caseStudies',
                    }}
                >
                    {ourComponents.map((item) => {
                        return (
                            <CaseStudiesCard
                                item={item}
                                key={item.sys.id}
                                learnMorelabel={LEARN_MORE_LABEL}
                                onLearnMoreClick={() => {
                                    handleModalOpen(item.sys.id);
                                }}
                                isShortView
                            />
                        );
                    })}
                </SwiperCarousel>
            </TileContainer>
        </>
    );
};

export default CaseStudiesContent;
